/**
 * 表情/贴纸面板纯逻辑（14.16b，可单测）：emoji 字符表、光标处插入、贴纸分组
 * 视图模型。组件（StickerPanel.tsx）保持纯展示。
 *
 * - emoji 是纯前端字符表（旧版内置网格同形态；具体字符集不要求逐字对齐——
 *   消息体就是文本，任何 emoji 都能发）。
 * - 贴纸分组：最近在前 + 篮子包按 mine 返回序；空包跳过（无可点内容）。
 *   包内条目统一折成 RecentSticker 形状——发送/上报都用它（stickerModel）。
 */

import type { RecentSticker, StickerMineSnapshot } from '~/chat/stickerModel';

/**
 * 内置 emoji 表（分组仅为编排可读，渲染平铺成一个网格——旧版同样单页网格）。
 * 全部是独立码位序列，插入 textarea 即普通文本。
 */
export const EMOJI_TABLE: readonly string[] = [
  // 笑脸/情绪
  '😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '😗', '😙',
  '😚', '🙂', '🤗', '🤔', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫',
  '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '🙁', '😖', '😞',
  '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪',
  '😵', '😡', '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '😇', '🥳', '🥺', '🤠', '🤡', '🤥',
  '🤫', '🤭', '🧐', '🤓', '😈', '👿', '👻', '💀', '👽', '🤖', '💩', '😺', '😸', '😹', '😻', '😼',
  // 手势/人
  '👋', '🤚', '✋', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️',
  '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '💪', '🦾', '🫶', '💅',
  // 心/符号
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘',
  '💝', '💯', '💢', '💥', '💫', '💦', '💨', '🕳️', '💣', '💬', '👁️', '🗨️', '🗯️', '💭', '💤', '✨',
  // 动物/自然
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈',
  '🙉', '🙊', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋',
  // 食物/活动/物件
  '🍎', '🍊', '🍋', '🍉', '🍇', '🍓', '🍑', '🍒', '🥝', '🍅', '🥑', '🌶️', '🍔', '🍟', '🍕', '🌭',
  '🍿', '🧂', '🥚', '🍳', '🥞', '🍜', '🍣', '🍦', '🍰', '🎂', '🍺', '🍻', '🥂', '🍷', '☕', '🧋',
  '⚽', '🏀', '🏈', '🎾', '🎮', '🎲', '🎯', '🎁', '🎉', '🎊', '🎈', '🏆', '🔥', '🌈', '☀️', '🌙',
  '⭐', '🌟', '⚡', '❄️', '☔', '🌊', '👑', '💍', '💎', '🔔', '🎵', '🎶', '📱', '💻', '⏰', '🚀',
];

/** 光标处插入 emoji（插入后光标落在 emoji 之后；caret 为 UTF-16 下标） */
export function insertEmojiAt(
  text: string,
  caret: number,
  emoji: string,
): { text: string; caret: number } {
  const at = Math.max(0, Math.min(caret, text.length));
  return { text: text.slice(0, at) + emoji + text.slice(at), caret: at + emoji.length };
}

/** 面板里的一个贴纸分组（最近 / 篮子包），导航条一键一组 */
export interface StickerGroupVM {
  /** 'recent' | 'pack:<id>'（导航条 key；活动组按它记） */
  readonly key: string;
  /** 包封面相对路径（最近组为空——导航条渲染 🕐） */
  readonly cover: string;
  readonly items: readonly RecentSticker[];
}

/** mine 快照 → 分组列表：最近在前（非空才出现）、空包跳过 */
export function stickerGroupsOf(snapshot: StickerMineSnapshot): StickerGroupVM[] {
  const groups: StickerGroupVM[] = [];
  if (snapshot.recent.length > 0) {
    groups.push({ key: 'recent', cover: '', items: snapshot.recent });
  }
  for (const pack of snapshot.packs) {
    if (pack.items.length === 0) continue;
    groups.push({
      key: `pack:${pack.id}`,
      cover: pack.cover,
      items: pack.items.map((it) => ({
        packId: pack.id,
        itemId: it.id,
        path: it.path,
        width: it.width,
        height: it.height,
      })),
    });
  }
  return groups;
}

/** 面板两页；null = 用户还没手动选过，跟随 defaultStickerTab 推导 */
export type StickerTab = 'emoji' | 'sticker';

/**
 * 面板打开时默认落哪一页（21b，旧版 `builtin_sticker_picker.dart` 的
 * `_selected = _sections.isEmpty ? 0 : 1` 同口径）：**有贴纸分组就落贴纸页**，
 * 一个都没有才回落表情页。
 *
 * snapshot=null（本次会话还没拉到）按「乐观有」处理先落贴纸页：拉到空会自动
 * 回落表情页（只闪一帧「加载中」，且 Worker 双层缓存基本是零 HTTP 的同步命中）。
 * 反过来默认表情页的话，有贴纸的用户每次开面板都要先看到表情页再被切走，更跳。
 *
 * 这取代了 14.16b 的刻意偏差「恒落表情页」——当时的顾虑是新版无内置贴纸资产、
 * 新用户贴纸页是空态，而旧版这条规则本身就把空态兜住了。
 */
export function defaultStickerTab(snapshot: StickerMineSnapshot | null): StickerTab {
  if (snapshot === null) return 'sticker';
  return stickerGroupsOf(snapshot).length > 0 ? 'sticker' : 'emoji';
}
